export interface TrendPoint {
  date: string;
  [key: string]: string | number;
}

export interface ChartMetric {
  key: string;
  label: string;
  color: string;
  type?: 'bar' | 'line';
}

interface AdminTrendChartProps {
  series: TrendPoint[];
  metrics: ChartMetric[];
  height?: number;
}

function formatShortDate(iso: string) {
  const [, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}`;
}

export function AdminTrendChart({ series, metrics, height = 180 }: AdminTrendChartProps) {
  if (!series.length || !metrics.length) return null;

  const barMetrics = metrics.filter((m) => m.type !== 'line');
  const lineMetrics = metrics.filter((m) => m.type === 'line');
  const barCount = barMetrics.length || 1;

  const padding = { top: 16, right: 12, bottom: 32, left: 40 };
  const innerW = Math.max(280, series.length * (barCount > 1 ? 22 : 12));
  const width = innerW + padding.left + padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxVal = Math.max(
    1,
    ...series.flatMap((row) => metrics.map((m) => Number(row[m.key] ?? 0))),
  );

  const yScale = (v: number) => padding.top + innerH - (v / maxVal) * innerH;
  const slotW = innerW / series.length;
  const barW = Math.max(2, (slotW - 4) / barCount);

  const labelStep = series.length <= 10 ? 1 : series.length <= 31 ? 5 : 10;

  const linePoints = (key: string) => series
    .map((row, i) => {
      const x = padding.left + i * slotW + slotW / 2;
      const y = yScale(Number(row[key] ?? 0));
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="admin-chart-wrap">
      <svg
        className="admin-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-hidden
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = padding.top + innerH * (1 - t);
          const val = Math.round(maxVal * t);
          return (
            <g key={t}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                className="admin-chart-grid"
              />
              <text x={padding.left - 6} y={y + 4} className="admin-chart-axis" textAnchor="end">
                {val}
              </text>
            </g>
          );
        })}

        {series.map((row, i) => {
          const slotX = padding.left + i * slotW;
          const values = barMetrics.map((m) => Number(row[m.key] ?? 0));
          const title = `${row.date}: ${metrics.map((m) => `${m.label} ${row[m.key] ?? 0}`).join(', ')}`;

          return (
            <g key={row.date}>
              {barMetrics.map((m, bi) => {
                const v = values[bi];
                const h = (v / maxVal) * innerH;
                const x = slotX + 2 + bi * barW;
                const y = padding.top + innerH - h;
                return (
                  <rect
                    key={m.key}
                    x={x}
                    y={y}
                    width={Math.max(1, barW - 1)}
                    height={Math.max(0, h)}
                    fill={m.color}
                    className="admin-chart-bar"
                  >
                    <title>{title}</title>
                  </rect>
                );
              })}
              {(i === 0 || i % labelStep === 0 || i === series.length - 1) && (
                <text
                  x={slotX + slotW / 2}
                  y={height - 8}
                  className="admin-chart-axis"
                  textAnchor="middle"
                >
                  {formatShortDate(row.date)}
                </text>
              )}
            </g>
          );
        })}

        {lineMetrics.map((m) => (
          <polyline
            key={m.key}
            points={linePoints(m.key)}
            fill="none"
            stroke={m.color}
            strokeWidth={2}
            className="admin-chart-line"
          />
        ))}
      </svg>

      <ul className="admin-chart-legend">
        {metrics.map((m) => (
          <li key={m.key}>
            <span
              className={`admin-chart-legend-swatch${m.type === 'line' ? ' admin-chart-legend-swatch--line' : ''}`}
              style={{ background: m.type === 'line' ? 'transparent' : m.color, borderColor: m.color }}
            />
            {m.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
